import { useState, useMemo } from 'react'
import { Card, Group, ScrollArea, Stack, Text, Badge } from '@mantine/core'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import type { BoardDto, TaskCardDto } from '@ledokol/contracts'
import { TaskCard } from './task-card'

interface BoardProps {
  board: BoardDto
  onTaskMove: (taskId: string, toColumnId: string) => void
  onTaskClick: (taskId: string) => void
}

export function KanbanBoard({ board, onTaskMove, onTaskClick }: BoardProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [activeTask, setActiveTask] = useState<TaskCardDto | null>(null)

  const tasksById = useMemo(() => {
    const m = new Map<string, { task: TaskCardDto; columnId: string }>()
    for (const c of board.columns) for (const t of c.tasks) m.set(t.id, { task: t, columnId: c.id })
    return m
  }, [board])

  function onDragStart(e: DragStartEvent) {
    const task = e.active.data.current?.task as TaskCardDto | undefined
    if (task) setActiveTask(task)
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveTask(null)
    const taskId = e.active.id as string
    const overId = e.over?.id as string | undefined
    if (!overId) return

    // overId — либо id колонки (Droppable), либо id задачи (Sortable item)
    let toColumnId = overId
    if (tasksById.has(overId)) {
      toColumnId = tasksById.get(overId)!.columnId
    }
    const current = tasksById.get(taskId)
    if (current && current.columnId !== toColumnId) {
      onTaskMove(taskId, toColumnId)
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <ScrollArea type="auto" style={{ height: 'calc(100vh - 200px)' }}>
        <Group align="flex-start" gap="md" wrap="nowrap" pb="md">
          {board.columns.map((col) => (
            <BoardColumnView key={col.id} column={col} onTaskClick={onTaskClick} />
          ))}
        </Group>
      </ScrollArea>
      <DragOverlay>
        {activeTask && <TaskCard task={activeTask} />}
      </DragOverlay>
    </DndContext>
  )
}

function BoardColumnView({
  column,
  onTaskClick,
}: {
  column: BoardDto['columns'][number]
  onTaskClick: (taskId: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, data: { type: 'column' } })

  return (
    <Card
      ref={setNodeRef}
      withBorder
      p="sm"
      radius="md"
      w={300}
      miw={300}
      style={{
        background: isOver ? 'var(--mantine-color-blue-0)' : 'var(--mantine-color-gray-0)',
        borderTop: `3px solid ${column.color ?? '#94a3b8'}`,
        transition: 'background .12s',
      }}
    >
      <Stack gap="xs" mb="sm">
        <Group justify="space-between">
          <Text fw={600} size="sm">
            {column.name}
          </Text>
          <Badge variant="light" color="gray" size="sm">
            {column.tasks.length}
          </Badge>
        </Group>
      </Stack>

      <SortableContext items={column.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <Stack gap="xs">
          {column.tasks.map((t) => (
            <TaskCard key={t.id} task={t} onClick={() => onTaskClick(t.id)} />
          ))}
          {column.tasks.length === 0 && (
            <Text size="xs" c="dimmed" ta="center" py="md">
              пусто
            </Text>
          )}
        </Stack>
      </SortableContext>
    </Card>
  )
}
